import * as THREE from 'three'
import type { SceneObject } from '@/types/scene'
import { collectPipePortsWorld } from '@/three/pipePorts'
import { buildOrthogonalPipeRoute } from '@/utils/pipeRoute'

export type PipeEndpoints = {
  start?: { objectId: string; portId: string }
  end?: { objectId: string; portId: string }
}

function findPortWorld(objects: SceneObject[], objectId: string, portId: string): THREE.Vector3 | null {
  for (const p of collectPipePortsWorld(objects)) {
    if (p.objectId === objectId && p.portId === portId) return p.position.clone()
  }
  return null
}

/**
 * 根据 params.endpoints 与当前端口世界坐标，重算 pipe_polyline 锚点与 waypoint（正交路由与创建时一致）。
 * 无有效端点时返回 null。
 */
export function buildLinkedPipePolylineUpdate(
  pipe: SceneObject,
  objects: SceneObject[],
  gridSize: number,
): { position: [number, number, number]; params: Record<string, any> } | null {
  if (pipe.type !== 'pipe_polyline') return null
  const ep = pipe.params?.endpoints as PipeEndpoints | undefined
  if (!ep?.start?.objectId || !ep?.end?.objectId) return null
  const startW = findPortWorld(objects, ep.start.objectId, ep.start.portId)
  const endW = findPortWorld(objects, ep.end.objectId, ep.end.portId)
  if (!startW || !endW) return null
  const route = buildOrthogonalPipeRoute(
    { position: { x: startW.x, y: startW.y, z: startW.z } },
    { position: { x: endW.x, y: endW.y, z: endW.z } },
    { gridSize, forceOrthogonal: true, minSegmentLength: 0.01 },
  )
  if (route.waypoints.length < 2) return null
  const anchor = route.waypoints[0]!
  const localWaypoints = route.waypoints.map((w) => ({ x: w.x - anchor.x, y: w.y - anchor.y, z: w.z - anchor.z }))
  const pos = pipe.position ?? [0, 0, 0]
  const nextPos: [number, number, number] = [anchor.x, anchor.y, anchor.z]
  const prevWp = JSON.stringify(pipe.params?.waypoints ?? [])
  const nextWp = JSON.stringify(localWaypoints)
  if (
    prevWp === nextWp
    && Math.abs(pos[0] - nextPos[0]) < 1e-3
    && Math.abs(pos[1] - nextPos[1]) < 1e-3
    && Math.abs(pos[2] - nextPos[2]) < 1e-3
  ) {
    return null
  }
  return {
    position: nextPos,
    params: {
      ...pipe.params,
      waypointSpace: 'local',
      waypoints: localWaypoints,
      endpoints: ep,
      autoFittings: [],
      topologyNodes: [],
    },
  }
}

/** 对所有带 endpoints 的折线管线生成更新列表（跳过无变化项） */
export function collectLinkedPipePolylineUpdates(
  objects: SceneObject[],
  gridSize: number,
): Array<{ id: string; position: [number, number, number]; params: Record<string, any> }> {
  const out: Array<{ id: string; position: [number, number, number]; params: Record<string, any> }> = []
  for (const o of objects) {
    if (o.type !== 'pipe_polyline') continue
    const ep = o.params?.endpoints as PipeEndpoints | undefined
    if (!ep?.start?.objectId || !ep?.end?.objectId) continue
    const patch = buildLinkedPipePolylineUpdate(o, objects, gridSize)
    if (patch) out.push({ id: o.id, position: patch.position, params: patch.params })
  }
  return out
}
